// What each preview shows on a slide with click animations.
//
// A slide's preview image is the whole slide, every shape showing. For slides
// with click animations the extension also uploads one drawing per click step
// (step 0 = before the first click) and reports which step the deck is on, so
// the preview can show what the audience sees and "next" can show what the
// next tap will reveal. A slide without step drawings works as before.

export type LiveStep = { slide: number; step: number };

export type PreviewSources = {
  slideImages: Map<number, string>;
  stepImages: Map<number, string[]>;
  // The step the deck last reported, if any. Only counts for its own slide.
  live: LiveStep | null;
  // Whether we arrived at the current slide by going back: Google shows a
  // slide you return to with all its animations played.
  cameBack: boolean;
};

// The step of `slide` on screen, or null when it has no step drawings.
export const stepOf = (slide: number, src: PreviewSources): number | null => {
  const frames = src.stepImages.get(slide);
  if (!frames || frames.length === 0) return null;
  if (src.live && src.live.slide === slide) {
    return Math.max(0, Math.min(frames.length - 1, src.live.step));
  }
  return src.cameBack ? frames.length - 1 : 0;
};

// The image of `slide` as the room sees it now.
export const shownImage = (slide: number, src: PreviewSources): string | undefined => {
  const step = stepOf(slide, src);
  return step === null ? src.slideImages.get(slide) : src.stepImages.get(slide)![step];
};

// How a slide looks on arriving at it: forward lands before its first click,
// back lands after its last.
const arrivalImage = (slide: number, src: PreviewSources, back: boolean): string | undefined => {
  const frames = src.stepImages.get(slide);
  if (frames && frames.length > 0) return back ? frames[frames.length - 1] : frames[0];
  return src.slideImages.get(slide);
};

// What one tap forward will show: the same slide's next step while it has
// steps left, else the next slide. Null at the very end of the deck.
export const afterNext = (
  slide: number,
  total: number | undefined,
  src: PreviewSources
): { slide: number; image?: string } | null => {
  const step = stepOf(slide, src);
  const frames = src.stepImages.get(slide);
  if (step !== null && frames && step < frames.length - 1) {
    return { slide, image: frames[step + 1] };
  }
  if (total && slide >= total) return null;
  return { slide: slide + 1, image: arrivalImage(slide + 1, src, false) };
};

// What one tap back will show. Null on the first slide before any click.
export const afterPrev = (slide: number, src: PreviewSources): { slide: number; image?: string } | null => {
  const step = stepOf(slide, src);
  if (step !== null && step > 0) return { slide, image: src.stepImages.get(slide)![step - 1] };
  if (slide <= 1) return null;
  return { slide: slide - 1, image: arrivalImage(slide - 1, src, true) };
};
